import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';

import { UseZepApiQueryOptions } from '../zep-api/types/ZepApiQuery';
import { hasZepetofulConfig } from '../zepetoful/client';
import { useZepetofulContext } from '../zepetoful/ZepetofulProvider';

export const QUERY_KEY_ZEPETOFUL = 'zepetoful';

export async function fetchZepetofulContent<T>(
  zepetofulClient: AxiosInstance,
  contentId: string,
  locale = 'en-US',
) {
  const { data } = await zepetofulClient.get<T>(contentId, {
    params: {
      locale: locale === 'en' ? 'en-US' : locale,
    },
  });
  return data;
}

export function useZepetofulContents<
  TQueryFnData extends readonly unknown[] | object = never,
  TData = TQueryFnData,
>(entryId: string, options?: UseZepApiQueryOptions<TQueryFnData, TData>) {
  const { client, locale } = useZepetofulContext();
  const enabled = hasZepetofulConfig && (options?.enabled ?? true);

  return useQuery(
    [QUERY_KEY_ZEPETOFUL, locale, entryId] as QueryKey,
    () => {
      return fetchZepetofulContent<TQueryFnData>(client, entryId, locale);
    },
    {
      // weegloo(구 CMS) 콘텐츠는 전부 비필수 마케팅 사이드 콘텐츠라 소비자가 data?. 로 방어한다.
      // 전역 QueryClient 의 useErrorBoundary:true 를 상속하면 콘텐츠 500 한 번에 GlobalErrorBoundary 로
      // 페이지 전체가 죽는다(PLAY-APP-AZY / WEB-APP-1RD). 기본적으로 에러를 페이지로 던지지 않는다.
      // 관측성은 앱 QueryCache onError 가 handled 로 Sentry 에 남겨 유지한다.
      useErrorBoundary: false,
      // 캐싱 강화 + 백그라운드 패치 억제 — 비필수 콘텐츠라 재방문·포커스·재연결마다
      // weegloo 를 다시 때릴 이유가 없다. 500/hang 노출 빈도도 함께 줄인다.
      staleTime: 10 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
      retry: 2,
      retryDelay: 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      ...options,
      enabled,
    },
  );
}
